import { combineLatest, forkJoin, from, Observable, of } from 'rxjs';
import {
  AnalyticsSchool,
  ClassroomAnalytics,
  configurationSelectors,
  documentId,
  Facets,
  LevelLocation,
  MetricTarget,
  Product,
  ProductLocation,
  StudentAnalytics,
  ViewLevel,
  WelGoal,
} from '@wam/shared';
import { catchError, concatMap, map, mergeMap, reduce, switchMap, take } from 'rxjs/operators';
import { ApiGatewayService } from '@wam/authentication';
import { Injectable, inject } from '@angular/core';
import { select } from '@ngrx/store';
import * as fromGoals from './state/goals.selectors';
import { AnalyticsQuery } from '@app/shared/analytics-query.model';
import { AssignmentsService } from '@app/shared/assignments.service';
import { AnalyticsService } from '@app/shared/services/analytics.service';
import { WithAuthService } from '@app/shared/services/with-auth.service';
import { Course } from '@app/shared/course.model';
import { chunk, isArray, isEmpty, omit } from 'lodash-es';
import { AssignmentBatchItemResponse, AssignmentResponse } from '@wap/catalog-v5';
import {
  to24HourFormatEndTime,
  to24HourFormatStartTime,
} from '@app/shared/availability-hours-helpers';
import { Toggles } from '@app/shared/models/toggles.enum';

type ChangeAssignmentOutput = {
  failures?: AssignmentBatchItemResponse[];
  successes: AssignmentResponse[];
};

type SetStudentLocationResult = {
  operationStatus: number;
  operationMessage: string;
  operationBody?: { student: string };
};

// Keeps each bulk request well inside the API Gateway timeout, since wel-event-bridge writes
// the locations of a request one at a time.
const STUDENT_LOCATIONS_CHUNK_SIZE = 10;
const LOCATION_SAVE_FAILED = 'Changing the student location failed. Please try again later.';

@Injectable({
  providedIn: 'root',
})
export class GoalsService extends WithAuthService {
  private apiGatewayService = inject(ApiGatewayService);
  private assignmentsService = inject(AssignmentsService);
  private analyticsService = inject(AnalyticsService);

  getHits(): Observable<
    [ClassroomAnalytics[] | StudentAnalytics[] | AnalyticsSchool[], number, Facets]
  > {
    return combineLatest([
      this.store.pipe(
        select(fromGoals.getSelectedFacets),
        map((selected) => AnalyticsQuery.fromFacets(this.removeUnassignedStudents(selected))),
      ),
      this.store.pipe(select(fromGoals.getPage)),
      this.store.pipe(select(fromGoals.getLevel)),
    ]).pipe(
      switchMap(([query, page, level]) => {
        switch (level) {
          case ViewLevel.schools:
            return this.analyticsService.getSchools(query, page);
          case ViewLevel.classes:
            return this.analyticsService.getClassesWithFacets(query, page);
          case ViewLevel.students:
            return this.analyticsService.getUsers(
              query,
              page,
              'user_students_with_course_assigned_facet',
            );
          default:
            return combineLatest([of([]), of(0), of({})]);
        }
      }),
      take(1),
    );
  }

  changeStudentAssignment(
    student: StudentAnalytics,
    courses: Course[],
  ): Observable<ChangeAssignmentOutput> {
    return this.storeVisibilityRulesInCatalog().pipe(
      switchMap((storeVisibilityInCatalog) => {
        const assignmentsToUnassign = courses
          .filter((c) => !c.assigned)
          .flatMap((c) => c.assignments);

        const assignmentsToAssign = courses
          .filter((c) => c.assigned)
          .filter((c) => c.product !== Product.wacs)
          .map((c) => {
            if (c.assignments && c.assignments[0]) {
              const assignmentToUpdate = this.withVisibilityRules(
                c.assignments[0],
                c,
                storeVisibilityInCatalog,
              );
              // @ts-ignore
              return this.changeSessionTime(assignmentToUpdate, c);
            }
            return this.createNewAssignment(c, student, storeVisibilityInCatalog);
          });

        if (isEmpty(assignmentsToUnassign) && isEmpty(assignmentsToAssign)) {
          return of({ successes: [] });
        }
        return this.assignmentsService.assignmentCreateUpdateDelete(
          assignmentsToAssign,
          assignmentsToUnassign,
          student.schools[0].uuid,
        );
      }),
    );
  }

  changeClassAssignment(
    students: StudentAnalytics[],
    courses: Course[],
  ): Observable<ChangeAssignmentOutput> {
    return this.storeVisibilityRulesInCatalog().pipe(
      switchMap((storeVisibilityInCatalog) => {
        const assignmentsToUnassign = courses
          .filter((c) => !c.assigned)
          .flatMap((c) => c.assignments);

        const assignmentsToAssign = courses
          .filter((c) => c.assigned)
          .filter((c) => c.product !== Product.wacs)
          .flatMap((c) => {
            if (c.assignments && c.assignments.length > 0) {
              return c.assignments.map((a) => {
                const assignmentToUpdate = this.withVisibilityRules(a, c, storeVisibilityInCatalog);
                // @ts-ignore
                return this.changeSessionTime(assignmentToUpdate, c);
              });
            }
            return students.map((student) =>
              this.createNewAssignment(c, student, storeVisibilityInCatalog),
            );
          }) as AssignmentResponse[];

        if (isEmpty(assignmentsToUnassign) && isEmpty(assignmentsToAssign)) {
          return of({ successes: [] });
        }

        return this.assignmentsService.assignmentCreateUpdateDelete(
          assignmentsToAssign,
          assignmentsToUnassign,
          students[0].schools[0].uuid,
          false,
        );
      }),
    );
  }

  getGoals(organization: string, entity: string): Observable<WelGoal[]> {
    return forkJoin([
      this.getGoalsForProduct(organization, entity, Product.erp),
      this.getGoalsForProduct(organization, entity, Product.ems),
      this.getGoalsForProduct(organization, entity, Product.smartstart),
    ]);
  }

  getGradeGoals(
    organization: string,
    entity: string,
    grade: string,
    product: Product,
  ): Observable<{ [key: string]: WelGoal[] }> {
    return forkJoin([
      this.getGradeGoalsForProduct(organization, entity, grade, product).pipe(
        map<WelGoal, { [key: string]: WelGoal[] }>((goal) => ({
          [`${product}_${grade}`]: [goal].map((g: WelGoal) => ({
            ...g,
            days: Math.round(g.playtimeWeek / g.playtimeDay),
          })),
        })),
      ),
    ]).pipe(map((val) => val.reduce((acc, curr) => ({ ...acc, ...curr }), {})));
  }

  saveGradeGoal(goal: WelGoal): Observable<WelGoal> {
    return this.apiGatewayService
      .put<WelGoal>(
        `achievement-v3/v3/wel/orgs/${goal.organizationKey}/products/${goal.productKey}/entities/${goal.entityKey}/grades/${goal.gradeKey}/goals`,
        {
          levelTargetKey: goal.levelTargetKey,
          playtimeWeek: goal.playtimeWeek,
          playtimeDay: goal.playtimeDay,
        },
        'welGoals',
      )
      .pipe(map((g) => ({ ...g, days: Math.round(g.playtimeWeek / g.playtimeDay) })));
  }

  saveGoal(organization: string, entity: string, goal: WelGoal): Observable<unknown> {
    return this.apiGatewayService.put(
      `achievement-v3/v3/wel/orgs/${organization}/products/${goal.productKey}/entities/${entity}/goals`,
      omit(goal, 'playtimeSchoolYear'),
      'welGoals',
    );
  }

  getTargets(): Observable<MetricTarget[][]> {
    return forkJoin([
      this.getTargetsForProduct(Product.erp),
      this.getTargetsForProduct(Product.ems),
      this.getTargetsForProduct(Product.smartstart),
    ]);
  }

  getOrgLocations(): Observable<ProductLocation[]> {
    return forkJoin([
      this.getLocationsForProduct(Product.erp),
      this.getLocationsForProduct(Product.ems),
      this.getLocationsForProduct(Product.smartstart),
    ]);
  }

  getStudentLocation(
    organization: string,
    entity: string,
    products: { erp: boolean; ems: boolean; smartstart: boolean },
  ): Observable<(ProductLocation | {})[]> {
    return forkJoin([
      products.erp ? this.getStudentLocationsForProduct(organization, entity, Product.erp) : of({}),
      products.ems ? this.getStudentLocationsForProduct(organization, entity, Product.ems) : of({}),
      products.smartstart
        ? this.getStudentLocationsForProduct(organization, entity, Product.smartstart)
        : of({}),
    ]);
  }

  saveLocation(organization: string, entity: string, location: LevelLocation, studentName: string) {
    const { product, levelId, locationId } = location;
    return this.apiGatewayService
      .post(
        `wel-event-bridge/v2/sequencer/orgs/${organization}/students/${entity}/products/${product}/levels/${levelId}/locations/${locationId}`,
        {},
      )
      .pipe(
        catchError(({ error }) =>
          of({ error: this.toLocationError(error?.description, studentName) }),
        ),
      );
  }

  /**
   * Sets the location of many students through the bulk endpoint. Chunks run one after another
   * because concurrent WEL location writes deadlock. Emits the error messages of failed students.
   */
  saveLocations(
    organization: string,
    students: StudentAnalytics[],
    location: LevelLocation,
  ): Observable<string[]> {
    const { product, levelId, locationId } = location;
    const studentNames = new Map(
      students.map((student) => [student.uuid, `${student.givenName} ${student.familyName}`]),
    );
    return from(
      chunk(
        students.map((student) => student.uuid),
        STUDENT_LOCATIONS_CHUNK_SIZE,
      ),
    ).pipe(
      concatMap((studentUuids) =>
        this.apiGatewayService
          .post<SetStudentLocationResult[]>(
            `wel-event-bridge/v2/sequencer/orgs/${organization}/products/${product}/levels/${levelId}/locations/${locationId}`,
            { students: studentUuids },
            'set-locations',
          )
          .pipe(
            map((results) =>
              results
                .filter(({ operationStatus }) => !(operationStatus >= 200 && operationStatus < 300))
                .map(({ operationMessage, operationBody }) =>
                  this.toLocationError(
                    operationMessage,
                    studentNames.get(operationBody?.student) ?? 'Student',
                  ),
                ),
            ),
            catchError(() => of([LOCATION_SAVE_FAILED])),
          ),
      ),
      reduce((errors, chunkErrors) => [...errors, ...chunkErrors], [] as string[]),
    );
  }

  getLock(organization: string, entity: string | string[]): Observable<{ [key: string]: boolean }> {
    const entities: string[] = isArray(entity) ? <string[]>entity : [<string>entity];
    return forkJoin(
      entities.map((entity) =>
        this.apiGatewayService
          .get<{
            locked: boolean;
          }>(`achievement-v3/v3/orgs/${organization}/entities/${entity}/locks`)
          .pipe(
            catchError(() => of({ locked: true })),
            map((response) => ({ [`${organization}_${entity}`]: response.locked })),
          ),
      ),
    ).pipe(
      mergeMap((res) => res),
      reduce((acc, curr) => ({ ...acc, ...curr }), {}),
    );
  }

  updateLock(organization: string, entity: string, locked: boolean): Observable<void> {
    return this.withUser().pipe(
      mergeMap((user) =>
        this.apiGatewayService.put<void>(
          `achievement-v3/v3/orgs/${organization}/entities/${entity}/locks`,
          { locked, userName: user.name, userRole: user.roles.join(',') },
          'entityLocks',
        ),
      ),
    );
  }

  private toLocationError(description: string | undefined, studentName: string): string {
    if (description?.includes('is currently in a session')) {
      return `${studentName} is currently in a session.`;
    }
    if (description?.includes('is not assigned to product')) {
      return `${studentName} is not assigned to this product.`;
    }
    return LOCATION_SAVE_FAILED;
  }

  private removeUnassignedStudents(selectedFacets: Facets): Facets {
    return {
      ...selectedFacets,
      unassignedStudents: [
        {
          value: 'false',
          display: 'Show Licensed',
        },
      ],
    };
  }

  private changeSessionTime(assignment: AssignmentResponse, course: Course): AssignmentResponse {
    return {
      ...assignment,
      metadata: { ...assignment.metadata, sessionTime: course.sessionTime * 60 },
    };
  }

  // The catalog assignment is the source of truth for visibility rules only while the curriculum
  // visibility service toggle is off; otherwise the existing catalog values are left untouched.
  private storeVisibilityRulesInCatalog(): Observable<boolean> {
    return this.store.pipe(
      select(configurationSelectors.isEnabled(Toggles.CURRICULUM_VISIBILITY_SERVICE_ENABLED)),
      take(1),
      map((serviceEnabled) => !serviceEnabled),
    );
  }

  private withVisibilityRules(
    assignment: AssignmentResponse,
    course: Course,
    storeVisibilityInCatalog: boolean,
  ): AssignmentResponse {
    if (!storeVisibilityInCatalog) {
      return { ...assignment };
    }
    return {
      ...assignment,
      visibilityHours:
        course.availableStartHour && course.availableEndHour
          ? {
              startTime: to24HourFormatStartTime(course.availableStartHour),
              endTime: to24HourFormatEndTime(course.availableEndHour),
            }
          : undefined,
      visibilityExcludedDays: course.visibilityExcludedDays,
    } as AssignmentResponse;
  }

  private createNewAssignment(
    course: Course,
    student: StudentAnalytics,
    storeVisibilityInCatalog = true,
  ): AssignmentResponse {
    const assignment = {
      organization: student.organization.uuid,
      user: student.uuid,
      documentId: documentId(course.product),
      metadata: {
        products: [course.product],
        sessionTime: course.sessionTime * 60,
      },
      school: student.schools[0].uuid,
    };

    if (!storeVisibilityInCatalog) {
      return assignment as AssignmentResponse;
    }

    return {
      ...assignment,
      visibilityHours:
        course.availableStartHour && course.availableEndHour
          ? {
              startTime: to24HourFormatStartTime(course.availableStartHour),
              endTime: to24HourFormatEndTime(course.availableEndHour),
            }
          : undefined,
      visibilityExcludedDays: course.visibilityExcludedDays ?? [],
    } as AssignmentResponse;
  }

  private getGradeGoalsForProduct(
    organization: string,
    entity: string,
    grade: string,
    product: Product,
  ): Observable<WelGoal> {
    return this.apiGatewayService.get<WelGoal>(
      `achievement-v3/v3/wel/orgs/${organization}/products/${product}/entities/${entity}/grades/${grade}/goals`,
    );
  }

  getGoalsForProduct(organization: string, entity: string, product: Product): Observable<WelGoal> {
    return this.apiGatewayService
      .get(
        `achievement-v3/v3/wel/orgs/${organization}/products/${product}/entities/${entity}/goals`,
      )
      .pipe(
        // @ts-ignore
        map((goal: WelGoal) => ({
          ...goal,
          days: Math.round(goal.playtimeWeek / goal.playtimeDay),
        })),
      );
  }

  getTargetsForProduct(product: Product): Observable<MetricTarget[]> {
    return this.apiGatewayService.get(
      `achievement-v3/v3/products/${product}/metrics/LEVEL/targets`,
    );
  }

  private getLocationsForProduct(product: Product): Observable<ProductLocation> {
    return this.withUser().pipe(
      mergeMap((user) =>
        this.apiGatewayService.get<ProductLocation>(
          `wel-event-bridge/v2/sequencer/orgs/${user.organization}/products/${product}/locations`,
        ),
      ),
    );
  }

  private getStudentLocationsForProduct(
    organization: string,
    entity: string,
    product: Product,
  ): Observable<ProductLocation | {}> {
    return this.apiGatewayService
      .get(
        `wel-event-bridge/v2/sequencer/orgs/${organization}/students/${entity}/products/${product}/locations`,
      )
      .pipe(catchError(() => of({})));
  }
}
